import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Inbox, Loader2, CheckCircle2, AlertTriangle, Send, Mail,
  RefreshCw, Globe, ChevronDown, History, FileText, Image as ImageIcon,
  BarChart3, BookOpen, Clock, ExternalLink, XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_PILL = {
  running:   { bg: '#eff4ff', fg: '#004ac6', label: 'Running' },
  completed: { bg: '#ecfdf5', fg: '#16a34a', label: 'Completed' },
  partial:   { bg: '#fef3c7', fg: '#92400e', label: 'Partial' },
  failed:    { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
};

const STAGE_LABEL = {
  text: 'Sections',
  images: 'Images',
  infographic: 'Infographic',
  references: 'References',
  done: 'Complete',
};

function fmtDuration(secs) {
  if (secs == null) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtRelative(iso) {
  if (!iso) return '—';
  try {
    const ts = new Date(iso).getTime();
    const delta = Date.now() - ts;
    if (delta < 60_000) return 'just now';
    if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
    if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
    return `${Math.floor(delta / 86_400_000)}d ago`;
  } catch {
    return iso;
  }
}

export default function ReportBuilderPage() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [forceRegen, setForceRegen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runs, setRuns] = useState({ active: [], history: [] });
  const [loadingRuns, setLoadingRuns] = useState(true);
  const pollRef = useRef(null);

  const loadCountries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/countries`, { headers: auth() });
      setCountries(data || []);
    } catch (e) {
      toast.error('Failed to load countries');
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/pipeline-runs?limit=10`, { headers: auth() });
      setRuns(data);
      return data;
    } catch (e) {
      return null;
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  useEffect(() => { loadCountries(); }, [loadCountries]);

  // Poll runs every 4s while there's an active job; stop when none.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await loadRuns();
      if (cancelled) return;
      const hasActive = (data?.active || []).length > 0;
      if (hasActive) {
        pollRef.current = setTimeout(tick, 4000);
      } else {
        pollRef.current = null;
      }
    };
    tick();
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [loadRuns]);

  const handleSubmit = async () => {
    if (!selectedId) { toast.error('Pick a country first'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/countries/${selectedId}/generate-full-blueprint`,
        { force_regenerate: forceRegen },
        { headers: auth() },
      );
      if (data.already_running) {
        toast.info('A pipeline is already running for that country.');
      } else {
        toast.success(data.detail || 'Pipeline started — check your inbox when it finishes.');
      }
      // Kick a fresh poll cycle immediately.
      await loadRuns();
      if (!pollRef.current) {
        const tick = async () => {
          const d = await loadRuns();
          if ((d?.active || []).length > 0) pollRef.current = setTimeout(tick, 4000);
          else pollRef.current = null;
        };
        pollRef.current = setTimeout(tick, 4000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start pipeline');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCountry = countries.find((c) => c.id === selectedId);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="report-builder-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-[#0b1c30] tracking-tight flex items-center gap-2">
          <Inbox className="w-6 h-6 text-[#7c3aed]" />
          Report Builder
        </h1>
        <p className="text-sm text-[#434655] mt-1 max-w-2xl leading-relaxed">
          Pick a country and kick off the full sequential blueprint —
          {' '}<strong className="text-[#0b1c30]">sections → images → infographic → references</strong>{' '}—
          one item at a time. You'll get an email when it's ready. Detailed content lives on the country page.
        </p>
      </div>

      {/* Builder card */}
      <div className="bg-white rounded-xl border border-[#c3c6d7]/30 p-5 shadow-sm" data-testid="builder-card">
        <div className="text-[10px] font-black uppercase tracking-widest text-[#737686] mb-3">New Build</div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Country</span>
            <div className="relative mt-1">
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="appearance-none w-full px-3 py-2.5 pr-9 rounded-lg border border-[#c3c6d7]/50 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]/30 focus:border-[#7c3aed]"
                data-testid="report-builder-country-select"
              >
                <option value="">— select a country or regional bloc —</option>
                {/* Regional blocs grouped at top so they're easy to spot. */}
                {countries.some((c) => c.is_region) && (
                  <optgroup label="Regional Blocs (multi-country)">
                    {countries.filter((c) => c.is_region).map((c) => (
                      <option key={c.id} value={c.id} disabled={c.pipeline_running}>
                        🌐 {c.name}
                        {typeof c.member_count === 'number' ? ` · ${c.member_count} members` : ''}
                        {c.sector ? ` · ${c.sector}` : ''}
                        {c.pipeline_running ? '  (running…)' : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {countries.some((c) => !c.is_region) && (
                  <optgroup label="Individual Countries">
                    {countries.filter((c) => !c.is_region).map((c) => (
                      <option key={c.id} value={c.id} disabled={c.pipeline_running}>
                        {c.name}{c.sector ? ` · ${c.sector}` : ''}{c.pipeline_running ? '  (running…)' : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737686] pointer-events-none" />
            </div>
          </label>
          <div className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Scope</span>
            <div className="mt-1 px-3 py-2.5 rounded-lg border border-[#eff4ff] bg-[#f8f9ff] text-sm font-bold text-[#0b1c30] flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#7c3aed]" />
              {selectedCountry?.is_region
                ? `Regional Bloc Blueprint · ${selectedCountry.member_count} member countries`
                : 'Full Blueprint (all sections in your tier)'}
            </div>
          </div>
        </div>

        <label className="flex items-start gap-2 text-[12px] text-[#434655] mt-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={forceRegen}
            onChange={(e) => setForceRegen(e.target.checked)}
            className="mt-0.5 accent-[#7c3aed]"
            data-testid="report-builder-force-checkbox"
          />
          <span>Regenerate completed sections too (otherwise they're skipped)</span>
        </label>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !selectedId || (selectedCountry?.pipeline_running)}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#7c3aed] hover:bg-[#6d28d9] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold uppercase tracking-widest transition"
            data-testid="report-builder-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? 'Starting…' : 'Start Build'}
          </button>
          <span className="text-[11px] text-[#737686] inline-flex items-center gap-1">
            <Mail className="w-3 h-3" /> You'll be emailed when it's done.
          </span>
        </div>
      </div>

      {/* Active jobs */}
      <div className="bg-white rounded-xl border border-[#c3c6d7]/30 p-5 shadow-sm" data-testid="active-jobs-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Currently Running</div>
          {loadingRuns && <Loader2 className="w-3 h-3 animate-spin text-[#737686]" />}
          <button onClick={loadRuns} className="ml-auto text-[10px] font-bold uppercase tracking-widest text-[#7c3aed] hover:bg-[#f5f3ff] px-2 py-1 rounded inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {(runs.active || []).length === 0 ? (
          <div className="text-xs text-[#737686] italic py-3">No pipelines are running right now.</div>
        ) : (
          <div className="space-y-3">
            {runs.active.map((j) => {
              const pct = j.total_steps > 0 ? Math.round((j.steps_done / j.total_steps) * 100) : 0;
              return (
                <div key={j.country_id} className="p-3 rounded-lg bg-gradient-to-br from-[#f8f9ff] to-white border border-[#eff4ff]" data-testid={`active-job-${j.country_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-bold text-[#0b1c30] text-sm">{j.country_name || '—'}</div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: STATUS_PILL.running.bg, color: STATUS_PILL.running.fg }}>
                      <Loader2 className="w-3 h-3 animate-spin" /> {STAGE_LABEL[j.stage] || j.stage}
                    </span>
                  </div>
                  <div className="text-xs text-[#434655] mt-1 truncate" title={j.current_label}>{j.current_label || '—'}</div>
                  <div className="mt-2 h-1.5 rounded-full bg-[#eff4ff] overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#004ac6] via-[#0891b2] to-[#7c3aed] transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[#737686] mt-1.5">
                    <span>{j.steps_done} / {j.total_steps} steps</span>
                    <span>{pct}% · started {fmtRelative(j.started_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-[#c3c6d7]/30 p-5 shadow-sm" data-testid="history-card">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-[#737686]" />
          <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Recent Builds</span>
        </div>
        {(runs.history || []).length === 0 ? (
          <div className="text-xs text-[#737686] italic py-3">No completed runs yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] font-black uppercase tracking-widest text-[#737686] border-b border-[#eff4ff]">
                  <th className="px-2 py-2">Country</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2"><FileText className="inline w-3 h-3" /> Text</th>
                  <th className="px-2 py-2"><ImageIcon className="inline w-3 h-3" /> Images</th>
                  <th className="px-2 py-2"><BarChart3 className="inline w-3 h-3" /> Infographic</th>
                  <th className="px-2 py-2"><BookOpen className="inline w-3 h-3" /> References</th>
                  <th className="px-2 py-2"><Clock className="inline w-3 h-3" /> Elapsed</th>
                  <th className="px-2 py-2">Finished</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {runs.history.map((r) => {
                  const pill = STATUS_PILL[r.status] || STATUS_PILL.failed;
                  const sectionsTotal = r.tier_keys_count || '—';
                  return (
                    <tr key={r.id} className="border-b border-[#f8f9ff] hover:bg-[#f8f9ff]/50" data-testid={`history-row-${r.id}`}>
                      <td className="px-2 py-2 font-bold text-[#0b1c30]">{r.country_name || '—'}</td>
                      <td className="px-2 py-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: pill.bg, color: pill.fg }}>
                          {r.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                          {r.status === 'partial' && <AlertTriangle className="w-3 h-3" />}
                          {r.status === 'failed' && <XCircle className="w-3 h-3" />}
                          {pill.label}
                        </span>
                      </td>
                      <td className="px-2 py-2">{r.text_done_count}/{sectionsTotal}</td>
                      <td className="px-2 py-2">{r.images_done_count}/{sectionsTotal}</td>
                      <td className="px-2 py-2">{r.infographic_done ? '✓' : '—'}</td>
                      <td className="px-2 py-2">{r.references_done ? '✓' : '—'}</td>
                      <td className="px-2 py-2 font-mono">{fmtDuration(r.duration_seconds)}</td>
                      <td className="px-2 py-2 text-[#737686]">{fmtRelative(r.finished_at)}</td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/platform/countries/${r.country_id}/blueprint`)}
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#7c3aed] hover:bg-[#f5f3ff] px-2 py-1 rounded"
                          data-testid={`view-blueprint-${r.country_id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[10px] text-[#737686] leading-relaxed">
        This page intentionally shows status only — the full report content lives in the Countries section so you can browse it whenever you want.
      </p>
    </div>
  );
}
